import { isDevMode, Type } from '@angular/core';

/** Clé unique identifiant un composant ouvrable dans une modale. */
export enum ModalComponentKey {
  AccountForm = 'AccountForm',
  AccountMoveAdd = 'AccountMoveAdd',
  AccountMoveAddInvoiceExtended = 'AccountMoveAddInvoiceExtended',
  AccountMoveAssign = 'AccountMoveAssign',
  AccountMoveAssignDetails = 'AccountMoveAssignDetails',
  AccountMoveAssignRepayment = 'AccountMoveAssignRepayment',
  AccountMoveAssignSlip = 'AccountMoveAssignSlip',
  AccountMoveAssignSlipDetails = 'AccountMoveAssignSlipDetails',
  CashControlForm = 'CashControlForm',
  CashJournalAdd = 'CashJournalAdd',
  ChequeRemittanceFormCheques = 'ChequeRemittanceFormCheques',
  ContactCreateContent = 'ContactCreateContent',
  ContactDetailsContent = 'ContactDetailsContent',
  ContractRejectReason = 'ContractRejectReason',
  ContractStatusHistoryEdit = 'ContractStatusHistoryEdit',
  ContractTerminationReason = 'ContractTerminationReason',
  CounselorChoiceQuoteOcealiz = 'CounselorChoiceQuoteOcealiz',
  CurrencyCreateContent = 'CurrencyCreateContent',
  CurrencyDetailsContent = 'CurrencyDetailsContent',
  DocumentEdit = 'DocumentEdit',
  InsuranceClaimRejectReason = 'InsuranceClaimRejectReason',
  InsuranceClaimStatusHistoryEdit = 'InsuranceClaimStatusHistoryEdit',
  LeadLostReason = 'LeadLostReason',
  PasswordManage = 'PasswordManage',
  PaymentAdd = 'PaymentAdd',
  SalesObjectivesRuleForm = 'SalesObjectivesRuleForm',
  SalesTeamExternalAccountCreate = 'SalesTeamExternalAccountCreate',
  SelectClaimTypeDocumentTypeEdit = 'SelectClaimTypeDocumentTypeEdit',
  SelectDocumentTypeEdit = 'SelectDocumentTypeEdit',
  SlipAdd = 'SlipAdd',
  UpdatePasswordAccount = 'UpdatePasswordAccount',
  UserExternalAccountCreate = 'UserExternalAccountCreate',
  LeadSetAsDuplicatesContentComponent = 'LeadSetAsDuplicatesContentComponent',
}

const REGISTRY = new Map<ModalComponentKey, Type<unknown>>();

/**
 * Marque un composant comme ouvrable dans une modale (`ModalContentComponent`).
 * À poser au-dessus de `@Component`. La clé doit être unique.
 */
export function ModalComponent(key: ModalComponentKey): ClassDecorator {
  return (target) => {
    const component = target as unknown as Type<unknown>;
    const existing = REGISTRY.get(key);
    // tolère la ré-évaluation du module (HMR / rebuild dev)
    if (existing && existing.name !== component.name) {
      throw new Error(
        `[ModalComponent] clé « ${key} » déjà utilisée par ${existing.name}, ` +
          `refusée pour ${component.name}.`,
      );
    }
    REGISTRY.set(key, component);
  };
}

export const modalComponentRegistry = {
  get(key: ModalComponentKey): Type<unknown> {
    const component = REGISTRY.get(key);
    if (!component) {
      throw new Error(
        `[ModalComponent] aucun composant enregistré pour la clé « ${key} ». ` +
          `Vérifier le décorateur @ModalComponent sur la classe cible.`,
      );
    }
    return component;
  },

  has: (key: ModalComponentKey): boolean => REGISTRY.has(key),

  /** Résolution depuis une string construite à l'exécution (typeahead). */
  resolve(raw: string): ModalComponentKey | undefined {
    const key = raw as ModalComponentKey;
    return REGISTRY.has(key) ? key : undefined;
  },

  /** Garde de démarrage : toutes les clés déclarées ont un composant. */
  assertComplete(): void {
    const missing = Object.values(ModalComponentKey).filter(
      (key) => !REGISTRY.has(key),
    );
    if (missing.length === 0) return;
    const message = `[ModalComponent] clés sans composant enregistré : ${missing.join(', ')}`;
    if (isDevMode()) throw new Error(message);
    console.error(message);
  },
};
